import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, ShieldCheck, Droplets, SearchCheck, CheckCircle2, Loader2,
  MapPin, Phone, Mail, Sun, CalendarCheck, PhoneCall, XCircle, Globe, Camera, BatteryCharging, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  nl: {
    badge: 'Solar Engineering Reinigingen',
    heroPersonal: (naam) => (<>Dag {naam},<br /><span className="text-orange-500">tijd om weer maximaal</span> op te brengen.</>),
    heroGeneric: (<>Uw zonnepanelen verdienen een <span className="text-orange-500">maximaal rendement</span> en een <span className="text-orange-500">lange levensduur</span>.</>),
    heroSub: 'Wij reinigen uw installatie professioneel — en u kiest zelf de formule die het best bij u past.',
    usps: ['Zuiver osmosewater', 'Geen krassen', "Voor & na foto's"],
    diffTitle: 'Het verschil dat u écht ziet',
    before: 'Voor', after: 'Na',
    yourData: 'Uw gegevens',
    prefilled: 'Alles klopt al voor u ingevuld — pas gerust aan waar nodig.',
    fillIn: 'Vul uw gegevens in — wij doen de rest.',
    firstName: 'Voornaam', lastName: 'Naam', email: 'E-mailadres', phone: 'Telefoon',
    panels: 'Aantal zonnepanelen', panelsPh: 'bv. 14',
    street: 'Straat', streetPh: 'Begin te typen...', nr: 'Nr.', postal: 'Postcode', city: 'Gemeente',
    floors: 'Aantal verdiepen', floorsPh: 'bv. 2', ladder: 'Bereikbaar met ladder',
    chooseFormula: 'Kies uw formule',
    formulaIntro: 'Hoe snel uw panelen vervuilen, hangt af van de ligging van uw dak: veel bomen in de buurt, open velden, landbouwstof of Saharazand zorgen voor snellere vervuiling. Kies de formule die het best bij uw situatie past — twijfelt u? Wij adviseren u graag.',
    mostChosen: 'Meest gekozen',
    formules: {
      eenmalig: { title: 'Eenmalig', lines: ['Eén professionele reinigingsbeurt van uw zonnepanelen', 'Inclusief visuele controle van zonnepanelen en constructie', 'Geen verdere verplichtingen'] },
      '2_yearly': { title: '2-Jaarlijks contract', lines: ['Om de 2 jaar een professionele reiniging van uw zonnepanelen', 'Wij nemen automatisch contact met u op', 'Altijd kosteloos annuleerbaar'] },
      '3_yearly': { title: '3-Jaarlijks contract', lines: ['Om de 3 jaar een professionele reiniging van uw zonnepanelen', 'Wij nemen automatisch contact met u op', 'Altijd kosteloos annuleerbaar'] },
    },
    submitBtn: 'Aanvraag verzenden',
    disclaimer: 'Vrijblijvend — u ontvangt eerst een offerte op maat, altijd kosteloos annuleerbaar',
    thanks: (naam) => `Bedankt, ${naam}!`,
    thanksSub: 'Uw reinigingsaanvraag is goed ontvangen. Ons team neemt zo snel mogelijk contact met u op om de reiniging van uw zonnepanelen in te plannen.',
    toWebsite: 'Naar onze website →',
    emailAlert: 'Vul een geldig e-mailadres in.',
    errorAlert: 'Er ging iets mis. Probeer opnieuw of mail ons op info@solar-engineering.be',
    notAvailable: 'Deze pagina is niet (meer) beschikbaar.',
    defaultName: 'zonnepaneleneigenaar',
    battAd: 'Ontdek ons assortiment thuisbatterijen',
    battBtn: 'Info aanvragen',
    battSent: 'Aangevraagd ✓',
    battTitle: 'Info over thuisbatterijen',
    battSub: 'Laat uw gegevens achter en wij nemen contact met u op.',
    battName: 'Naam', battPhone: 'Telefoonnummer', battEmail: 'E-mailadres',
    battSubmit: 'Verzenden',
    battThanks: 'Bedankt! Wij nemen snel contact met u op.',
  },
  fr: {
    badge: 'Solar Engineering Nettoyages',
    heroPersonal: (naam) => (<>Bonjour {naam},<br /><span className="text-orange-500">il est temps de produire</span> à nouveau au maximum.</>),
    heroGeneric: (<>Vos panneaux solaires méritent un <span className="text-orange-500">rendement maximal</span> et une <span className="text-orange-500">longue durée de vie</span>.</>),
    heroSub: 'Nous nettoyons votre installation de manière professionnelle — et vous choisissez la formule qui vous convient le mieux.',
    usps: ['Eau osmosée pure', 'Sans rayures', 'Photos avant & après'],
    diffTitle: 'La différence que vous voyez vraiment',
    before: 'Avant', after: 'Après',
    yourData: 'Vos coordonnées',
    prefilled: 'Tout est déjà pré-rempli pour vous — modifiez si nécessaire.',
    fillIn: 'Remplissez vos coordonnées — nous nous occupons du reste.',
    firstName: 'Prénom', lastName: 'Nom', email: 'Adresse e-mail', phone: 'Téléphone',
    panels: 'Nombre de panneaux solaires', panelsPh: 'p.ex. 14',
    street: 'Rue', streetPh: 'Commencez à taper...', nr: 'N°', postal: 'Code postal', city: 'Commune',
    floors: "Nombre d'étages", floorsPh: 'p.ex. 2', ladder: 'Accessible avec une échelle',
    chooseFormula: 'Choisissez votre formule',
    formulaIntro: "La vitesse d'encrassement de vos panneaux dépend de la situation de votre toit : beaucoup d'arbres à proximité, champs ouverts, poussières agricoles ou sable du Sahara accélèrent l'encrassement. Choisissez la formule qui correspond le mieux à votre situation — un doute ? Nous vous conseillons volontiers.",
    mostChosen: 'Le plus choisi',
    formules: {
      eenmalig: { title: 'Unique', lines: ['Un nettoyage professionnel de vos panneaux solaires', 'Contrôle visuel des panneaux solaires et de la structure inclus', 'Sans aucun engagement'] },
      '2_yearly': { title: 'Contrat bisannuel', lines: ['Un nettoyage professionnel de vos panneaux tous les 2 ans', 'Nous vous contactons automatiquement', 'Toujours annulable sans frais'] },
      '3_yearly': { title: 'Contrat trisannuel', lines: ['Un nettoyage professionnel de vos panneaux tous les 3 ans', 'Nous vous contactons automatiquement', 'Toujours annulable sans frais'] },
    },
    submitBtn: 'Envoyer la demande',
    disclaimer: "Sans engagement — vous recevez d'abord un devis sur mesure, toujours annulable sans frais",
    thanks: (naam) => `Merci, ${naam} !`,
    thanksSub: 'Votre demande de nettoyage a bien été reçue. Notre équipe vous contactera dans les plus brefs délais pour planifier le nettoyage de vos panneaux solaires.',
    toWebsite: 'Vers notre site web →',
    emailAlert: 'Veuillez saisir une adresse e-mail valide.',
    errorAlert: 'Une erreur est survenue. Réessayez ou envoyez-nous un e-mail à info@solar-engineering.be',
    notAvailable: "Cette page n'est plus disponible.",
    defaultName: 'propriétaire de panneaux solaires',
    battAd: 'Découvrez notre gamme de batteries domestiques',
    battBtn: 'Demander des infos',
    battSent: 'Demandé ✓',
    battTitle: 'Infos sur les batteries domestiques',
    battSub: 'Laissez vos coordonnées et nous vous contacterons.',
    battName: 'Nom', battPhone: 'Numéro de téléphone', battEmail: 'Adresse e-mail',
    battSubmit: 'Envoyer',
    battThanks: 'Merci ! Nous vous contacterons rapidement.',
  },
  en: {
    badge: 'Solar Engineering Cleanings',
    heroPersonal: (naam) => (<>Hi {naam},<br /><span className="text-orange-500">time to perform</span> at full power again.</>),
    heroGeneric: (<>Your solar panels deserve <span className="text-orange-500">maximum output</span> and a <span className="text-orange-500">long lifespan</span>.</>),
    heroSub: 'We clean your installation professionally — and you choose the formula that suits you best.',
    usps: ['Pure osmosis water', 'No scratches', 'Before & after photos'],
    diffTitle: 'The difference you can really see',
    before: 'Before', after: 'After',
    yourData: 'Your details',
    prefilled: 'Everything is pre-filled for you — feel free to adjust where needed.',
    fillIn: 'Fill in your details — we take care of the rest.',
    firstName: 'First name', lastName: 'Last name', email: 'Email address', phone: 'Phone',
    panels: 'Number of solar panels', panelsPh: 'e.g. 14',
    street: 'Street', streetPh: 'Start typing...', nr: 'No.', postal: 'Postal code', city: 'City',
    floors: 'Number of floors', floorsPh: 'e.g. 2', ladder: 'Accessible by ladder',
    chooseFormula: 'Choose your formula',
    formulaIntro: 'How quickly your panels get dirty depends on the location of your roof: many trees nearby, open fields, agricultural dust or Sahara sand cause faster soiling. Choose the formula that best fits your situation — in doubt? We are happy to advise you.',
    mostChosen: 'Most popular',
    formules: {
      eenmalig: { title: 'One-time', lines: ['One professional cleaning of your solar panels', 'Includes visual inspection of solar panels and mounting structure', 'No further obligations'] },
      '2_yearly': { title: '2-Yearly contract', lines: ['A professional cleaning of your panels every 2 years', 'We contact you automatically', 'Always cancellable free of charge'] },
      '3_yearly': { title: '3-Yearly contract', lines: ['A professional cleaning of your panels every 3 years', 'We contact you automatically', 'Always cancellable free of charge'] },
    },
    submitBtn: 'Send request',
    disclaimer: 'No obligation — you first receive a tailored quote, always cancellable free of charge',
    thanks: (naam) => `Thank you, ${naam}!`,
    thanksSub: 'Your cleaning request has been received. Our team will contact you as soon as possible to schedule the cleaning of your solar panels.',
    toWebsite: 'Visit our website →',
    emailAlert: 'Please enter a valid email address.',
    errorAlert: 'Something went wrong. Please try again or email us at info@solar-engineering.be',
    notAvailable: 'This page is no longer available.',
    defaultName: 'solar panel owner',
    battAd: 'Discover our range of home batteries',
    battBtn: 'Request info',
    battSent: 'Requested ✓',
    battTitle: 'Home battery info',
    battSub: 'Leave your details and we will contact you.',
    battName: 'Name', battPhone: 'Phone number', battEmail: 'Email address',
    battSubmit: 'Send',
    battThanks: 'Thank you! We will contact you soon.',
  },
};

const FORMULE_ICONS = {
  eenmalig: [Sparkles, SearchCheck, XCircle],
  '2_yearly': [CalendarCheck, PhoneCall, ShieldCheck],
  '3_yearly': [CalendarCheck, PhoneCall, ShieldCheck],
};
const USP_ICONS = [Droplets, ShieldCheck, Camera];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[13px] font-semibold text-slate-600 mb-1.5 tracking-wide uppercase">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-900 shadow-sm outline-none transition-all focus:border-orange-400 focus:ring-4 focus:ring-orange-100 placeholder:text-slate-300";

export default function ReinigingAanvraag() {
  const { leadId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(null);
  const [formule, setFormule] = useState('3_yearly');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [lang, setLang] = useState('nl');
  const [battOpen, setBattOpen] = useState(false);
  const [battForm, setBattForm] = useState({ name: '', phone: '', email: '' });
  const [battSending, setBattSending] = useState(false);
  const [battSent, setBattSent] = useState(false);
  const suggestTimer = React.useRef(null);
  const suggestBoxRef = React.useRef(null);
  const t = T[lang];

  const battRequest = async () => {
    if (leadId) {
      setBattSending(true);
      try {
        await axios.post(`${API}/api/public/battery-info`, { lead_id: leadId, language: lang });
        setBattSent(true);
      } catch { alert(t.errorAlert); } finally { setBattSending(false); }
    } else {
      setBattOpen(true);
    }
  };

  const battSubmit = async () => {
    if (!battForm.email || !battForm.email.includes('@')) { alert(t.emailAlert); return; }
    setBattSending(true);
    try {
      await axios.post(`${API}/api/public/battery-info`, { ...battForm, language: lang });
      setBattSent(true);
      setTimeout(() => setBattOpen(false), 2500);
    } catch { alert(t.errorAlert); } finally { setBattSending(false); }
  };

  const onStreetChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, street: val }));
    clearTimeout(suggestTimer.current);
    if (val.trim().length < 3) { setSuggestions([]); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/lookup/address`, { params: { q: val, limit: 6, country: 'be' } });
        setSuggestions(res.data.results || []);
      } catch { setSuggestions([]); }
    }, 300);
  };

  const pickSuggestion = (s) => {
    setForm(f => ({
      ...f, street: s.street || f.street,
      number: s.number || f.number,
      postal_code: s.postal_code || f.postal_code,
      city: s.city || f.city,
    }));
    setSuggestions([]);
  };

  useEffect(() => {
    const close = (e) => {
      if (suggestBoxRef.current && !suggestBoxRef.current.contains(e.target)) setSuggestions([]);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const url = leadId ? `${API}/api/public/reiniging-aanvraag/${leadId}` : `${API}/api/public/reiniging-aanvraag`;
    axios.get(url)
      .then(res => {
        setData(res.data);
        if (res.data.submitted) setDone(true);
        setForm({
          first_name: res.data.first_name || '', last_name: res.data.last_name || '',
          email: res.data.email || '',
          phone: res.data.phone || '', street: res.data.street || '', number: res.data.number || '',
          postal_code: res.data.postal_code || '', city: res.data.city || '',
          panel_count: res.data.panel_count || '',
          floors: '', ladder_access: true,
        });
      })
      .catch(() => setNotFound(true));
  }, [leadId]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!leadId && (!form.email || !form.email.includes('@'))) {
      alert(t.emailAlert);
      return;
    }
    setSending(true);
    try {
      const url = leadId ? `${API}/api/public/reiniging-aanvraag/${leadId}` : `${API}/api/public/reiniging-aanvraag`;
      await axios.post(url, { ...form, contract_type: formule, language: lang });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      alert(t.errorAlert);
    } finally {
      setSending(false);
    }
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#f6f4ef] flex items-center justify-center p-6 font-[Manrope,ui-sans-serif]">
      <p className="text-slate-500 text-lg">{t.notAvailable}</p>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#f6f4ef] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
    </div>
  );

  const voornaam = form?.first_name || data.first_name || t.defaultName;

  return (
    <div className="min-h-screen bg-[#f6f4ef] text-slate-900" style={{ fontFamily: "'Manrope','Segoe UI',ui-sans-serif,system-ui" }} data-testid="reiniging-landing-page">
      <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&display=swap" rel="stylesheet" />

      {/* Topbar */}
      <header className="w-full bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          {data.logo_url ? (
            <img src={data.logo_url} alt={data.company_name} className="h-16 md:h-20 object-contain" data-testid="landing-logo" />
          ) : (
            <span className="text-xl font-extrabold text-[#12275c]">SOLAR <span className="text-orange-500">Engineering</span></span>
          )}
          {/* Thuisbatterij banner */}
          <div className="hidden md:flex items-center gap-3 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-5 py-3 mx-4" data-testid="battery-banner">
            <BatteryCharging className="w-7 h-7 text-orange-500 flex-shrink-0" />
            <span className="text-sm font-bold text-[#12275c] leading-snug max-w-[220px]">{t.battAd}</span>
            <button type="button" onClick={battRequest} disabled={battSending || (battSent && !!leadId)}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-all whitespace-nowrap ${battSent && leadId
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-200 active:scale-95'}`}
              data-testid="battery-info-btn">
              {battSending ? <Loader2 className="w-4 h-4 animate-spin" /> : (battSent && leadId ? t.battSent : t.battBtn)}
            </button>
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden sm:flex flex-col items-start gap-1 text-sm text-slate-500">
              <a href={`tel:${data.company_phone}`} className="flex items-center gap-1.5 hover:text-orange-600 transition-colors"><Phone className="w-4 h-4 flex-shrink-0" />{data.company_phone}</a>
              <a href="https://www.solar-engineering.be" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-orange-600 transition-colors" data-testid="landing-website-link"><Globe className="w-4 h-4 flex-shrink-0" />www.solar-engineering.be</a>
              <a href={`mailto:${data.company_email}`} className="flex items-center gap-1.5 hover:text-orange-600 transition-colors"><Mail className="w-4 h-4 flex-shrink-0" />{data.company_email}</a>
            </div>
            <div className="flex items-center rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="landing-lang-switch">
              {['nl', 'fr', 'en'].map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${lang === l
                    ? 'bg-[#12275c] text-white shadow' : 'text-slate-500 hover:text-slate-800'}`}
                  data-testid={`landing-lang-${l}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Thuisbatterij banner mobiel */}
      <div className="md:hidden mx-5 mt-4 flex items-center gap-3 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3" data-testid="battery-banner-mobile">
        <BatteryCharging className="w-6 h-6 text-orange-500 flex-shrink-0" />
        <span className="text-sm font-bold text-[#12275c] leading-snug flex-1">{t.battAd}</span>
        <button type="button" onClick={battRequest} disabled={battSending || (battSent && !!leadId)}
          className={`rounded-full px-3.5 py-2 text-xs font-bold transition-all whitespace-nowrap ${battSent && leadId
            ? 'bg-green-100 text-green-700'
            : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-200'}`}
          data-testid="battery-info-btn-mobile">
          {battSent && leadId ? t.battSent : t.battBtn}
        </button>
      </div>

      {/* Thuisbatterij popup (generieke pagina) */}
      {battOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setBattOpen(false)}>
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl p-7" onClick={(e) => e.stopPropagation()} data-testid="battery-popup">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <BatteryCharging className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-extrabold text-[#12275c]">{t.battTitle}</h3>
              </div>
              <button type="button" onClick={() => setBattOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="battery-popup-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {battSent ? (
              <div className="mt-6 text-center py-6" data-testid="battery-popup-success">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="font-bold text-[#12275c]">{t.battThanks}</p>
              </div>
            ) : (
              <>
                <p className="mt-2 text-sm text-slate-500">{t.battSub}</p>
                <div className="mt-5 space-y-3.5">
                  <Field label={t.battName}>
                    <input className={inputCls} value={battForm.name} onChange={(e) => setBattForm(f => ({ ...f, name: e.target.value }))} data-testid="battery-name" />
                  </Field>
                  <Field label={t.battPhone}>
                    <input className={inputCls} value={battForm.phone} onChange={(e) => setBattForm(f => ({ ...f, phone: e.target.value }))} placeholder="+32 ..." data-testid="battery-phone" />
                  </Field>
                  <Field label={t.battEmail}>
                    <input type="email" className={inputCls} value={battForm.email} onChange={(e) => setBattForm(f => ({ ...f, email: e.target.value }))} placeholder="uw@email.be" data-testid="battery-email" />
                  </Field>
                </div>
                <button type="button" onClick={battSubmit} disabled={battSending}
                  className="mt-6 w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-60 transition-all text-white font-extrabold py-3.5 shadow-lg shadow-orange-200"
                  data-testid="battery-popup-submit">
                  {battSending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t.battSubmit}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {done ? (
        /* ===== BEDANKT ===== */
        <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="landing-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#12275c]">{t.thanks(voornaam)}</h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{t.thanksSub}</p>
          <a href="https://www.solar-engineering.be" className="inline-block mt-10 rounded-full bg-orange-500 hover:bg-orange-600 transition-colors px-8 py-3.5 text-white font-bold shadow-lg shadow-orange-200">
            {t.toWebsite}
          </a>
        </div>
      ) : (
        <>
          {/* ===== HERO ===== */}
          <section className="max-w-6xl mx-auto px-5 pt-10 md:pt-16 grid md:grid-cols-2 gap-10 items-start">
            <div className="md:sticky md:top-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-100 text-orange-700 px-4 py-1.5 text-sm font-bold mb-5">
                <Sparkles className="w-4 h-4" /> {t.badge}
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08] text-[#12275c]" data-testid="landing-greeting">
                {data.first_name ? t.heroPersonal(voornaam) : t.heroGeneric}
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-md">{t.heroSub}</p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {t.usps.map((usp, i) => {
                  const I = USP_ICONS[i];
                  return (
                    <span key={usp} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3.5 py-1.5 text-sm font-semibold text-slate-700 shadow-sm">
                      <I className="w-4 h-4 text-orange-500" /> {usp}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-orange-200/60 to-sky-200/60 blur-xl" />
              <div className="relative rounded-[1.8rem] bg-white shadow-xl shadow-slate-200/70 border border-slate-100 p-6 sm:p-8" data-testid="landing-hero-form">
              <h2 className="text-lg font-extrabold text-[#12275c]">{t.yourData}</h2>
              <p className="text-sm text-slate-500 mt-1">
                {data.first_name ? t.prefilled : t.fillIn}
              </p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field label={t.firstName}><input className={inputCls} value={form.first_name} onChange={set('first_name')} data-testid="landing-first-name" /></Field>
                <Field label={t.lastName}><input className={inputCls} value={form.last_name} onChange={set('last_name')} data-testid="landing-last-name" /></Field>
                {!data.email && (
                  <div className="sm:col-span-2">
                    <Field label={t.email}>
                      <input type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="uw@email.be" data-testid="landing-email" />
                    </Field>
                  </div>
                )}
                <Field label={t.phone}><input className={inputCls} value={form.phone} onChange={set('phone')} placeholder="+32 ..." data-testid="landing-phone" /></Field>
                <Field label={t.panels}>
                  <div className="relative">
                    <Sun className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
                    <input type="number" min="1" className={inputCls + ' pl-11'} value={form.panel_count} onChange={set('panel_count')} placeholder={t.panelsPh} data-testid="landing-panel-count" />
                  </div>
                </Field>
                <div className="sm:col-span-2 grid grid-cols-[1fr_90px] gap-4">
                  <div className="relative" ref={suggestBoxRef}>
                    <Field label={t.street}>
                      <input className={inputCls} value={form.street} onChange={onStreetChange}
                        autoComplete="off" name="street-no-autofill" placeholder={t.streetPh} data-testid="landing-street" />
                    </Field>
                    {suggestions.length > 0 && (
                      <div className="absolute z-30 left-0 right-0 mt-1.5 rounded-xl bg-white border border-slate-200 shadow-xl overflow-hidden" data-testid="street-suggestions">
                        {suggestions.map((s, i) => (
                          <button key={i} type="button" onClick={() => pickSuggestion(s)}
                            className="w-full flex items-start gap-2.5 px-4 py-2.5 text-left hover:bg-orange-50 transition-colors border-b border-slate-50 last:border-0"
                            data-testid={`street-suggestion-${i}`}>
                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-500" />
                            <span>
                              <span className="block text-[14px] font-semibold text-slate-800">{s.street}{s.number ? ` ${s.number}` : ''}</span>
                              <span className="block text-xs text-slate-400">{[s.postal_code, s.city].filter(Boolean).join(' ')}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Field label={t.nr}><input className={inputCls} value={form.number} onChange={set('number')} autoComplete="off" data-testid="landing-number" /></Field>
                </div>
                <Field label={t.postal}><input className={inputCls} value={form.postal_code} onChange={set('postal_code')} data-testid="landing-postal" /></Field>
                <Field label={t.city}><input className={inputCls} value={form.city} onChange={set('city')} data-testid="landing-city" /></Field>
                <Field label={t.floors}>
                  <input type="number" min="0" className={inputCls} value={form.floors} onChange={set('floors')} placeholder={t.floorsPh} data-testid="landing-floors" />
                </Field>
                <div className="flex items-end">
                  <button type="button" onClick={() => setForm(f => ({ ...f, ladder_access: !f.ladder_access }))}
                    className={`w-full flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-all ${form.ladder_access
                      ? 'border-orange-400 bg-orange-50 shadow-sm' : 'border-slate-200 bg-white hover:border-orange-200'}`}
                    data-testid="landing-ladder-toggle">
                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${form.ladder_access ? 'border-orange-500 bg-orange-500' : 'border-slate-300'}`}>
                      {form.ladder_access && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </span>
                    <span className="text-[15px] font-semibold text-slate-700 text-left">{t.ladder}</span>
                  </button>
                </div>
              </div>

              {/* Formules */}
              <h2 className="text-lg font-extrabold text-[#12275c] mt-8">{t.chooseFormula}</h2>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t.formulaIntro}</p>
              <div className="mt-4 grid gap-3">
                {['eenmalig', '2_yearly', '3_yearly'].map((key) => {
                  const active = formule === key;
                  const f = t.formules[key];
                  const icons = FORMULE_ICONS[key];
                  return (
                    <button key={key} type="button" onClick={() => setFormule(key)}
                      className={`relative text-left rounded-2xl border-2 p-5 transition-all duration-200 ${active
                        ? 'border-orange-500 bg-orange-50/70 shadow-lg shadow-orange-100 -translate-y-0.5'
                        : 'border-slate-200 bg-white hover:border-orange-300 hover:shadow-md'}`}
                      data-testid={`formule-${key}`}>
                      {key === '3_yearly' && (
                        <span className="absolute -top-3 left-4 rounded-full bg-[#12275c] text-white text-[11px] font-bold px-3 py-1">{t.mostChosen}</span>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="font-extrabold text-[#12275c]">{f.title}</span>
                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${active ? 'border-orange-500 bg-orange-500' : 'border-slate-300'}`}>
                          {active && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </span>
                      </div>
                      <ul className="mt-3 space-y-2">
                        {f.lines.map((text, i) => {
                          const I = icons[i];
                          return (
                            <li key={text} className="flex items-start gap-2 text-[13px] leading-snug text-slate-600">
                              <I className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-500" />{text}
                            </li>
                          );
                        })}
                      </ul>
                    </button>
                  );
                })}
              </div>

              <button onClick={submit} disabled={sending}
                className="mt-8 w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:opacity-60 transition-all text-white text-lg font-extrabold py-5 shadow-xl shadow-orange-200 active:scale-[0.99]"
                data-testid="landing-submit-btn">
                {sending ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : t.submitBtn}
              </button>
              <p className="mt-4 text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> {t.disclaimer}
              </p>
              </div>
            </div>
          </section>

          {/* ===== VOOR / NA ===== */}
          <section className="max-w-6xl mx-auto px-5 mt-16 md:mt-24 pb-24">
            <h2 className="text-lg font-extrabold text-[#12275c] text-center">{t.diffTitle}</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 md:gap-6 max-w-3xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-lg group">
                <img src="/landing/voor.jpg" alt={t.before} className="w-full h-52 md:h-64 object-cover" />
                <span className="absolute top-3 left-3 rounded-full bg-slate-900/80 backdrop-blur px-4 py-1 text-white text-sm font-bold">{t.before}</span>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <img src="/landing/na.jpg" alt={t.after} className="w-full h-52 md:h-64 object-cover" />
                <span className="absolute top-3 left-3 rounded-full bg-orange-500 px-4 py-1 text-white text-sm font-bold">{t.after}</span>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="bg-[#12275c] text-slate-300 py-8 text-center text-sm">
        <p className="font-bold text-white">{data.company_name}</p>
        <p className="mt-1.5 flex items-center justify-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Weverstraat 41 A, 1761 Borchtlombeek</p>
        <p className="mt-1">{data.company_email} · {data.company_phone}</p>
        <a href="https://www.solar-engineering.be" target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 font-semibold transition-colors" data-testid="landing-footer-website">
          <Globe className="w-3.5 h-3.5" /> www.solar-engineering.be
        </a>
      </footer>
    </div>
  );
}
